import { equals } from 'ramda';
import { Dispatch } from 'shared/common/state';
import { trackApplyDatesModal } from 'shared/containers/FiltersContainer/tracking/trackApplyDatesModal';
import { setLocationCookies } from 'shared/containers/LocationDetectModalModalContainer/setLocationCookies';
import { EObjectType, IJsonQueryDeveloper } from 'shared/json_query';
import { selectSource } from 'shared/selectors';
import { fetchCommercialBreadcrumbsIfNeeded } from 'shared/services/fetchCommercialBreadcrumbs';
import { fetchDeveloperInfoRedesignedSafe, fetchDeveloperInfoSafe } from 'shared/services/fetchDeveloperInfo';
import { fetchEarlyAccessDataIfNeeded } from 'shared/services/fetchEarlyAccessData';
import { fetchNewobjectsBuilderIfNeeded } from 'shared/services/fetchNewobjectsBuilder';
import { fetchOffers, prepareJsonQuery } from 'shared/services/fetchOffers';
import { fetchOldQuickLinksSafe } from 'shared/services/fetchOldQuickLinks';
import { fetchQuickLinksSafeIfNeeded } from 'shared/services/fetchQuickLinks';
import { fetchRecommendedVillagesSafe } from 'shared/services/fetchRecommendedVillages';
import { fetchSimilarNewobjectsIfNeeded } from 'shared/services/fetchSimilarNewobjects';
import { fetchSuburbanBuildersProjectsSafe } from 'shared/services/fetchSuburbanBuildersProjects';
import { fetchVillageInfoSafe } from 'shared/services/fetchVillageInfo';
import { IDeveloperInfo, IDeveloperInfoRedesigned } from 'shared/types/developerInfo';
import { TLocation } from 'shared/types/location';
import { IOffersData } from 'shared/types/offersData';
import { IRecommendedVillages } from 'shared/types/recommendedVillages';
import { TThunkAction } from 'shared/types/redux';
import { ISearchMeta } from 'shared/types/searchMeta';
import { ISuburbanProjectSuggest } from 'shared/types/suburbanBuildersProjects';
import { IVillageInfo } from 'shared/types/villageInfo';
import { cutDescriptionFromOffers } from 'shared/utils';
import { displayConsultantPhone } from 'shared/utils/displayNewbuildingConsultantPhone';
import { getLocationName } from 'shared/utils/filters';
import { getRegionId } from 'shared/utils/geo';
import { getClientNavigationUrl } from 'shared/utils/getClientNavigationUrl';
import { getSeoUrlFromFullUrl } from 'shared/utils/getSeoUrlFromFullUrl';
import { saveHistorySearch } from 'shared/utils/history_search';
import { isDeveloperPage } from 'shared/utils/isDeveloperPage';
import { isRedirectNeeded } from 'shared/utils/isRedirectNeeded';
import { isSubdomainRedirectNeeded } from 'shared/utils/isSubdomainRedirectNeeded';
import { offersDecorator } from 'shared/utils/offersDecorator';
import { cancelPendingPageView, trackPageViewDeferringEmpty } from 'shared/utils/tracking/deferredPageView';

import { incrementAnimationCounter } from '../consultantCianChat';
import { setMlSearchSessionGuid } from '../mlSearchSessionGuid';
import { setLoading } from './setLoading';
import { FILTERS_SEARCH_SEARCH_REQUEST_FINISHED } from './types';
import {
  getDateRangeValue,
  getTermsValue,
  isCommercial,
  isSuburban,
  JsonQuery,
  offerTypeFromJsonQuery,
  TJsonQuery,
} from '../../../packages/JsonQuery';
import { setEarlyAccessData } from '../earlyAccess';
import { resetJournalHighlights } from '../journal';

interface IParams {
  jsonQuery: TJsonQuery;
  location: TLocation;
  searchMeta: ISearchMeta;
  withSource?: boolean;
}

export function makeSearch({ jsonQuery, location, searchMeta, withSource }: IParams): TThunkAction<Promise<void>> {
  return async (dispatch: Dispatch, getState, context) => {
    cancelPendingPageView();
    dispatch(setLoading());

    let state = getState();

    if (areDatesChanged(state.results.jsonQuery, jsonQuery)) {
      trackApplyDatesModal();
    }

    const source = withSource ? selectSource(state) : undefined;
    const { fullUrl } = searchMeta;
    const clientNavigationUrl = getClientNavigationUrl(fullUrl);
    const { config, journalHighlights, currentSubdomain, liquiditySource } = state;
    const isDeveloperHeaderRedesigned = Boolean(config.get<boolean>('Newbuilding.SerpDeveloperHeader.Enabled'));

    const isCommercialBypass = !!config.get<boolean>('isNewCommercialSerpDesign.Desktop.Enabled');

    /**
     * Смена субдомена (смена города) невозможна клиентской навигацией, поэтому редирект по субдомену
     * выполняется в том числе и в обход коммерческого bypass'а
     */
    if (
      fullUrl &&
      (isSubdomainRedirectNeeded(fullUrl) || (!isCommercialBypass && isRedirectNeeded(config, jsonQuery, fullUrl)))
    ) {
      setLocationCookies(getRegionId(location), getLocationName(location));
      window.location.assign(fullUrl);

      return;
    }

    const isAdditionalTop3Disabled = config.get('serp.isAdditionalTop3Enabled') !== 'true';

    if (
      window.__reloadHeader__ &&
      jsonQuery.region &&
      !equals(jsonQuery.region.value, state.results.jsonQuery.region?.value)
    ) {
      window.__reloadHeader__();
    }

    const data: IOffersData = await fetchOffers(context, {
      jsonQuery: prepareJsonQuery({ jsonQuery, location }),
      liquiditySource,
    });

    /**
     * Сохранение текущего поиска для истории поисков
     */
    saveHistorySearch(data.seoData.h1, data.fullUrl, data.jsonQuery);

    /**
     * Если текущий путь не чпу, сбрасываем имеющиеся статьи журнала
     */
    if (data.fullUrl.includes('/cat.php') && journalHighlights.length > 0) {
      dispatch(resetJournalHighlights());
    }

    const suggestions = data.suggestOffersSerializedList;

    data.offersSerialized = cutDescriptionFromOffers(data.offersSerialized, state.userAgent);

    if (suggestions) {
      data.suggestOffersSerializedList = cutDescriptionFromOffers(suggestions, state.userAgent);
    }

    // Обработка объявлений
    offersDecorator(data.offersSerialized, isAdditionalTop3Disabled);

    let villageInfo: IVillageInfo | null = null;
    if (data.jsonQuery.kp_id) {
      const prevVillageInfo = state.results.villageInfo ?? null;

      villageInfo =
        prevVillageInfo?.id === data.jsonQuery.kp_id.value
          ? prevVillageInfo
          : await fetchVillageInfoSafe(context, { id: data.jsonQuery.kp_id.value });
    }

    let developerInfo: IDeveloperInfo | null = null;
    let developerInfoRedesigned: IDeveloperInfoRedesigned | null = null;

    if (data.jsonQuery.geo && isDeveloperPage(data.jsonQuery)) {
      const id = (data.jsonQuery.geo.value[0] as IJsonQueryDeveloper).id;

      if (isDeveloperHeaderRedesigned) {
        developerInfoRedesigned = await fetchDeveloperInfoRedesignedSafe(context, {
          subdomain: currentSubdomain,
          id,
        });
      } else {
        developerInfo = await fetchDeveloperInfoSafe(context, {
          subdomain: currentSubdomain,
          id,
        });
      }
    }

    const prevSeoUrl = getSeoUrlFromFullUrl(state.results.fullUrl);

    const [similarNewobjects, similarNewobjectsFromDeveloper, earlyAccessData, commercialBreadcrumbs] =
      await Promise.all([
        fetchSimilarNewobjectsIfNeeded(context, data),
        fetchNewobjectsBuilderIfNeeded(context, data, getRegionId(location)),
        fetchEarlyAccessDataIfNeeded(context, {
          jsonQuery: data.jsonQuery,
          regionId: getRegionId(location),
        }),
        fetchCommercialBreadcrumbsIfNeeded(
          context,
          { subdomain: currentSubdomain, path: data.fullUrl },
          isCommercial(offerTypeFromJsonQuery(data.jsonQuery)),
        ),
      ]);

    dispatch(setEarlyAccessData(earlyAccessData));

    const isSuburbanSearch = isSuburban(offerTypeFromJsonQuery(data.jsonQuery));

    state = getState();

    const nextSeoUrl = getSeoUrlFromFullUrl(fullUrl);

    let quickLinks = state.results.quickLinks;
    let oldQuickLinks = state.results.oldQuickLinks;

    if (prevSeoUrl !== nextSeoUrl) {
      quickLinks = await fetchQuickLinksSafeIfNeeded(context, {
        subdomain: currentSubdomain,
        path: nextSeoUrl,
      });

      oldQuickLinks =
        quickLinks && quickLinks.length > 0
          ? null
          : await fetchOldQuickLinksSafe(context, {
              subdomain: currentSubdomain,
              jsonQuery: data.jsonQuery,
            });
    }

    document.title = data.seoData.title || document.title;

    if (clientNavigationUrl) {
      window.history.pushState({}, document.title, clientNavigationUrl);
    }

    const regionId = getRegionId(location);

    const recommendedVillages: IRecommendedVillages | null = isSuburbanSearch
      ? await fetchRecommendedVillagesSafe(context, {
          jsonQuery: new JsonQuery(data.jsonQuery).setRegion([Math.abs(regionId)]).toJSON(),
        })
      : null;

    const objectType = getTermsValue('object_type')(data.jsonQuery);

    const suburbanBuildersProjects: ISuburbanProjectSuggest[] = objectType?.includes(EObjectType.Land)
      ? await fetchSuburbanBuildersProjectsSafe(context, {
          regionId,
        })
      : [];

    dispatch({
      type: FILTERS_SEARCH_SEARCH_REQUEST_FINISHED,
      data,
      newbuildingInfo: null,
      developerInfo,
      developerInfoRedesigned,
      villageInfo,
      similarNewobjects,
      similarNewobjectsFromDeveloper,
      quickLinks,
      oldQuickLinks,
      recommendedVillages,
      suburbanBuildersProjects,
      newbuildingLayouts: null,
      commercialBreadcrumbs,
    });
    dispatch(incrementAnimationCounter());
    dispatch(setMlSearchSessionGuid(data.mlSearchSessionGuid || null));

    state = getState();

    const consultantPhone = config.get<string>('newbuildingConsultant.phone.moscowAndRegion');
    const consultantPhoneEnabledRegions = config.get<number[]>('newbuildingConsultant.phone.enabledRegions') || [];
    displayConsultantPhone(state, consultantPhoneEnabledRegions, consultantPhone);

    trackPageViewDeferringEmpty(
      {
        jsonQueryFromFilters: jsonQuery,
        label: searchMeta.fullUrl,
        results: state.results,
        breadcrumbs: state.breadcrumbs,
        user: state.user,
        profileSessionKey: state.profileSessionKey,
        mlRankingGuid: data.mlRankingGuid || null,
        mlRankingModelVersion: data.mlRankingModelVersion || null,
        mlSearchSessionGuid: data.mlSearchSessionGuid || null,
        searchRequestId: data.searchRequestId,
        extensions: data.extensionTypes || undefined,
        location: state.currentLocation,
        source,
      },
      config,
    );
  };
}

function areDatesChanged(prevJsonQuery: TJsonQuery, nextJsonQuery: TJsonQuery) {
  const { gte: resultsDateFrom, lt: resultsDateTo } = getDateRangeValue('dates')(prevJsonQuery) || {};
  const { gte: filtersDateFrom, lt: filtersDateTo } = getDateRangeValue('dates')(nextJsonQuery) || {};

  return resultsDateFrom !== filtersDateFrom || resultsDateTo !== filtersDateTo;
}
